import {
  DocumentExtractedData,
  DocumentTypeExtractionField,
  ExtractionFieldDataType,
} from "@/prisma-package/src/utils/document-type-builtins";

// Shared limits for admin-defined extraction fields. Client-safe: imported by
// the field editor, the zod action schemas, and the extraction pipeline.
export const MAX_EXTRACTION_FIELDS = 10;
export const MAX_FIELD_LABEL_LENGTH = 60;
export const MAX_FIELD_INSTRUCTION_LENGTH = 500;
export const MAX_FIELD_VALUE_LENGTH = 500;

// Lowercase snake_case, deliberately distinct from the uppercase document
// type key regex — keys are JSON-schema property names and Mongo JSON keys.
export const EXTRACTION_FIELD_KEY_REGEX = /^[a-z][a-z0-9_]{1,39}$/;

export const EXTRACTION_FIELD_DATA_TYPES = [
  "text",
  "number",
  "date",
  "currency",
] as const;

export const EXTRACTION_FIELD_DATA_TYPE_LABELS: Record<
  ExtractionFieldDataType,
  string
> = {
  text: "Text",
  number: "Number",
  date: "Date",
  currency: "Currency",
};

// Keys that would shadow the built-in extraction outputs or document fields.
// Compared via canonicalizeFieldKey so "document_id", "documentId", and
// "DOCUMENT_ID" all collide.
const RESERVED_CANONICAL_KEYS = new Set([
  "documentid",
  "vendor",
  "vendorname",
  "othervendornames",
  "type",
  "category",
]);

const canonicalizeFieldKey = (key: string): string =>
  key.toLowerCase().replace(/_/g, "");

export const isReservedExtractionFieldKey = (key: string): boolean =>
  RESERVED_CANONICAL_KEYS.has(canonicalizeFieldKey(key));

/**
 * Derive a field key from a human label ("Invoice Total" -> "invoice_total"),
 * suffixing with _2, _3, ... when the slug collides with an existing key.
 */
export const toFieldKey = (
  label: string,
  existingKeys: string[] = []
): string => {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([^a-z])/, "f$1")
    .slice(0, 40);
  if (!base) return "";

  const taken = new Set(existingKeys.map(canonicalizeFieldKey));
  if (
    !taken.has(canonicalizeFieldKey(base)) &&
    !isReservedExtractionFieldKey(base)
  ) {
    return base;
  }
  for (let i = 2; i < 100; i += 1) {
    const candidate = `${base.slice(0, 40 - `_${i}`.length)}_${i}`;
    if (
      !taken.has(canonicalizeFieldKey(candidate)) &&
      !isReservedExtractionFieldKey(candidate)
    ) {
      return candidate;
    }
  }
  return "";
};

/**
 * Validate a full field list. Returns an error message, or null when valid.
 * Shared by the action zod schemas (superRefine) and the editor UI.
 */
export const validateExtractionFields = (
  fields: DocumentTypeExtractionField[]
): string | null => {
  if (fields.length > MAX_EXTRACTION_FIELDS) {
    return `At most ${MAX_EXTRACTION_FIELDS} extraction fields per document type`;
  }
  const seen = new Set<string>();
  for (const field of fields) {
    if (!EXTRACTION_FIELD_KEY_REGEX.test(field.key)) {
      return `Field key "${field.key}" must be lowercase snake_case (2-40 chars, starting with a letter)`;
    }
    if (isReservedExtractionFieldKey(field.key)) {
      return `Field key "${field.key}" is reserved`;
    }
    const canonical = canonicalizeFieldKey(field.key);
    if (seen.has(canonical)) {
      return `Duplicate field key "${field.key}"`;
    }
    seen.add(canonical);
    const label = field.label.trim();
    if (!label || label.length > MAX_FIELD_LABEL_LENGTH) {
      return `Field "${field.key}" needs a label of 1-${MAX_FIELD_LABEL_LENGTH} characters`;
    }
    if (
      field.instruction &&
      field.instruction.length > MAX_FIELD_INSTRUCTION_LENGTH
    ) {
      return `Field "${field.key}" instruction is over ${MAX_FIELD_INSTRUCTION_LENGTH} characters`;
    }
  }
  return null;
};

const NUMERIC_VALUE_REGEX = /^-?\d+(\.\d+)?$/;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Light normalization of an extracted value by dataType. Best-effort only:
 * when a value doesn't normalize cleanly it is returned trimmed, never
 * dropped — the user corrects it in the UI.
 */
export const normalizeFieldValue = (
  dataType: ExtractionFieldDataType,
  raw: string
): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";

  if (dataType === "number" || dataType === "currency") {
    const stripped = trimmed
      .replace(/^[^\d\-.(]*/, "")
      .replace(/[,\s]/g, "")
      .replace(/^\((.*)\)$/, "-$1");
    return NUMERIC_VALUE_REGEX.test(stripped) ? stripped : trimmed;
  }

  if (dataType === "date") {
    if (ISO_DATE_REGEX.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
      const day = `${parsed.getDate()}`.padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return trimmed;
  }

  return trimmed;
};

/**
 * Sanitize a raw AI `fields` object against the type's field spec: keep only
 * spec keys, stringify, trim, drop empties, and normalize by dataType.
 */
export const sanitizeExtractedFields = (
  spec: DocumentTypeExtractionField[] | null | undefined,
  raw: unknown
): DocumentExtractedData => {
  const result: DocumentExtractedData = {};
  if (!spec?.length || typeof raw !== "object" || raw === null) return result;

  const record = raw as Record<string, unknown>;
  for (const field of spec) {
    const value = record[field.key];
    if (value === null || value === undefined) continue;
    if (
      typeof value !== "string" &&
      typeof value !== "number" &&
      typeof value !== "boolean"
    ) {
      continue;
    }
    const normalized = normalizeFieldValue(field.dataType, String(value)).slice(
      0,
      MAX_FIELD_VALUE_LENGTH
    );
    if (normalized) result[field.key] = normalized;
  }
  return result;
};

/**
 * Apply a patch to an extracted-data bag: string sets the key, null deletes
 * it. Returns a new object; empty strings are treated as deletes so a cleared
 * input removes the key rather than storing "".
 */
export const mergeExtractedData = (
  existing: DocumentExtractedData | null | undefined,
  patch: Record<string, string | null>
): DocumentExtractedData => {
  const result: DocumentExtractedData = { ...(existing ?? {}) };
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || value.trim() === "") {
      delete result[key];
    } else {
      result[key] = value.trim().slice(0, MAX_FIELD_VALUE_LENGTH);
    }
  }
  return result;
};

/**
 * Keep only the entries of `values` whose keys are in the spec, dropping
 * empties. Used when saving so values extracted for a different type (the
 * user changed the type in the verification modal) don't leak into storage.
 */
export const pickSpecFieldValues = (
  values: Record<string, string> | null | undefined,
  spec: DocumentTypeExtractionField[] | null | undefined
): DocumentExtractedData => {
  const result: DocumentExtractedData = {};
  if (!values || !spec?.length) return result;
  for (const field of spec) {
    const value = values[field.key]?.trim();
    if (value) result[field.key] = value.slice(0, MAX_FIELD_VALUE_LENGTH);
  }
  return result;
};

/**
 * Humanize a field key for display when its spec entry no longer exists
 * ("invoice_total" -> "Invoice Total").
 */
export const humanizeFieldKey = (key: string): string =>
  key
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
